import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useKothLeaderboard, useStats } from "@/lib/hooks/useApi";
import { DatePickerCalendar } from "@/components/DatePickerCalendar";
import AnimatedLoading from "@/components/AnimatedLoading";
import { DefaultButton } from "@/components/DefaultButton";
import { DailyLeaderboard } from "./-components/daily-leaderboard";
import { DailyHighestStreak } from "./-components/daily-highest-streak";
import type { CustomLeaderboard } from "@/routes/(game)/leaderboard/-components/types";
import { usePrivy } from "@privy-io/react-auth";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { t } from "@/lib/i18n";
import { toast } from "sonner";

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

const getUtcEpochFromDate = (date: Date) => {
  const d = dayjs(date);
  return Math.floor(Date.UTC(d.year(), d.month(), d.date(), 0, 0, 0) / 1000);
};

const getDateFromEpoch = (epoch?: number) => {
  if (!epoch || !Number.isFinite(epoch)) return undefined;
  const utcDate = dayjs.unix(epoch).utc();
  return new Date(Date.UTC(utcDate.year(), utcDate.month(), utcDate.date()));
};

type KothLeaderboardSearch = {
  date?: number;
  tab?: "daily" | "highestStreak";
};

export const Route = createFileRoute("/(game)/koth/leaderboard")({
  validateSearch: (search): KothLeaderboardSearch => {
    const numericDate = Number(
      typeof search.date === "string" || typeof search.date === "number"
        ? search.date
        : undefined,
    );

    const result: KothLeaderboardSearch = {};

    if (Number.isFinite(numericDate) && numericDate > 0) {
      result.date = Math.floor(numericDate);
    }

    if (
      typeof search.tab === "string" &&
      (search.tab === "daily" || search.tab === "highestStreak")
    ) {
      result.tab = search.tab;
    }

    return result;
  },
  component: RouteComponent,
});

const UNVERIFIED_TOAST_ID = "toast:leaderboard-unverified";

function RouteComponent() {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const walletAddress = user?.wallet?.address ?? "";
  const search = Route.useSearch();
  const [activeTab, setActiveTab] = useState<"daily" | "highestStreak">(
    search.tab || "daily",
  );
  const hasInitializedRef = useRef(false);

  const { data: stats } = useStats(walletAddress);
  const gameWalletAddress = stats?.embeddedWallet;

  useEffect(() => {
    if (!walletAddress || stats?.isVerified !== false) {
      toast.dismiss(UNVERIFIED_TOAST_ID);
      return;
    }

    toast.warning("YOUR ACCOUNT IS UNVERIFIED", {
      id: UNVERIFIED_TOAST_ID,
      description:
        "Link Twitter/X and at least 1 other social to get verified and receive weekly rewards.",
      duration: Infinity,
      closeButton: false,
      dismissible: false,
      className: "flex flex-col",
      action: (
        <div className="flex w-full justify-end space-x-1">
          <DefaultButton
            variant="white"
            className="uppercase text-black"
            onClick={() => toast.dismiss(UNVERIFIED_TOAST_ID)}
          >
            Close
          </DefaultButton>
          <DefaultButton
            className="uppercase"
            onClick={() => {
              toast.dismiss(UNVERIFIED_TOAST_ID);
              navigate({ to: "/profile", search: { socialLink: true } });
            }}
          >
            Verify My Account
          </DefaultButton>
        </div>
      ),
    });
  }, [stats?.isVerified, walletAddress, navigate]);

  // Sync activeTab with search param
  useEffect(() => {
    if (
      search.tab &&
      (search.tab === "daily" || search.tab === "highestStreak")
    ) {
      setActiveTab(search.tab);
    }
  }, [search.tab]);

  const setSearchDate = useCallback(
    (date: Date) => {
      const epoch = getUtcEpochFromDate(date);

      if (search.date === epoch) {
        return;
      }

      navigate({
        from: Route.fullPath,
        replace: true,
        search: (prev) => ({
          ...prev,
          date: epoch,
        }),
      });
    },
    [navigate, search.date],
  );

  // Calculate epoch for API call: use URL date if available, otherwise use today
  const epoch = useMemo(() => {
    if (search.date && Number.isFinite(search.date)) {
      return search.date;
    }
    return getUtcEpochFromDate(new Date());
  }, [search.date]);

  // Determine the 'by' parameter based on active tab
  const byParam = useMemo(() => {
    return activeTab === "daily" ? "trophy" : "streak";
  }, [activeTab]);

  const leaderboardQuery = useKothLeaderboard({
    epoch,
    by: byParam,
    loaded: true,
    tier: 1,
  });

  // Only show data if the URL date matches the query epoch (prevents showing wrong data during sync)
  const shouldShowData = useMemo(() => {
    if (search.date && Number.isFinite(search.date)) {
      return search.date === epoch;
    }
    return false;
  }, [search.date, epoch]);

  // Sync URL with backend's startTimestamp only on initial load
  useEffect(() => {
    if (search.date && Number.isFinite(search.date)) {
      hasInitializedRef.current = true;
      return;
    }

    if (!hasInitializedRef.current && !leaderboardQuery.isLoading) {
      if (leaderboardQuery.data?.startTimestamp) {
        const backendEpoch = leaderboardQuery.data.startTimestamp;
        const dateFromBackend = getDateFromEpoch(backendEpoch);
        if (dateFromBackend) {
          setSearchDate(dateFromBackend);
          hasInitializedRef.current = true;
        }
      } else {
        setSearchDate(new Date());
        hasInitializedRef.current = true;
      }
    }
  }, [
    leaderboardQuery.data?.startTimestamp,
    leaderboardQuery.isLoading,
    search.date,
    setSearchDate,
  ]);

  const rows: CustomLeaderboard[] = useMemo(() => {
    // Only use data if we should show it (prevents showing wrong data during sync)
    if (!shouldShowData) {
      return [];
    }
    const ranking = leaderboardQuery.data?.leaderboard;
    if (!Array.isArray(ranking)) {
      return [];
    }
    return ranking.map((item, index) => ({
      id: index + 1,
      position: index + 1,
      isCurrentUser: item.userPublicKey === gameWalletAddress,
      ...item,
    }));
  }, [leaderboardQuery.data?.leaderboard, gameWalletAddress, shouldShowData]);

  // Show loading if query is loading OR if we're still syncing the date
  if (leaderboardQuery.isLoading || !shouldShowData) {
    return <AnimatedLoading />;
  }

  if (leaderboardQuery.error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 bg-[#1D2A52]">
        <p className="text-white font-semibold text-center">
          {t("kothLeaderboard.error.failedToLoad")}
        </p>
        <div className="flex flex-col items-center justify-center gap-2">
          <button
            onClick={() => leaderboardQuery.refetch()}
            className="mt-4 px-4 py-2 rounded-md bg-white/10 text-white text-sm hover:bg-white/20 transition"
          >
            {t("kothLeaderboard.error.tryAgain")}
          </button>
          <button className="text-white/70 text-xs">
            or{" "}
            <span
              className="text-blue-400 hover:text-blue-500 hover:cursor-pointer hover:underline active:scale-95 transition"
              onClick={() => navigate({ to: "/koth" })}
            >
              {t("kothLeaderboard.error.joinKoth")}
            </span>{" "}
            {t("kothLeaderboard.error.whileWeFixIssue")}
          </button>
        </div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div
        className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4 relative"
        style={{
          backgroundImage:
            "url('https://cdn.navalabs.io/assets/bg-live-tournament_JeXUeg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="w-full flex justify-between items-center absolute left-0 top-4 z-50">
          <FloatingLeftButton
            onClick={() => navigate({ to: "/koth" })}
            icon={
              <svg
                className="w-4 h-4"
                viewBox="0 0 9 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                  fill="white"
                />
              </svg>
            }
          />
          <DatePickerCalendar
            className="pe-2"
            value={getDateFromEpoch(search.date) ?? new Date()}
            onChange={setSearchDate}
          />
        </div>
        <div className="flex flex-col items-center justify-center gap-3">
          <img
            src="https://cdn.navalabs.io/assets/trophy-rush_WsDPjc.png"
            alt="KOTH Trophy"
            className="h-16 w-16 object-contain"
          />
          <div className="flex flex-col items-center justify-center">
            <p className="text-xl text-white font-semibold">
              {t("kothLeaderboard.emptyState.noDataToDisplay")}
            </p>
            <p className="text-white/70 text-xs text-center mx-4">
              {t("kothLeaderboard.emptyState.jumpBackIntoKoth")}
            </p>
          </div>
          <DefaultButton
            onClick={() => navigate({ to: "/koth" })}
            className="mt-4 px-4 py-2"
          >
            {t("kothLeaderboard.emptyState.joinKoth")}
          </DefaultButton>
        </div>
      </div>
    );
  }

  const formatCountdown = (seconds: number) => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const days = Math.floor(safeSeconds / 86400);
    const hours = Math.floor((safeSeconds % 86400) / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  return (
    <div
      className="flex-1 min-h-0 flex flex-col relative"
      style={{
        backgroundImage:
          "url('https://cdn.navalabs.io/assets/bg-live-tournament_JeXUeg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full flex justify-between items-center absolute left-0 top-4 z-50">
        <FloatingLeftButton
          onClick={() => navigate({ to: "/koth" })}
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />
      </div>
      <div className="absolute right-0 top-14 z-100">
        <DatePickerCalendar
          className="pe-2 z-100"
          value={getDateFromEpoch(search.date) ?? new Date()}
          onChange={setSearchDate}
        />
      </div>

      {/* Title and Timer */}
      <div className="mt-24 px-2 flex w-full items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-1">
          <h2 className="text-3xl font-bold text-white uppercase text-center">
            {activeTab === "daily"
              ? t("kothLeaderboard.leaderboard.dailyLeaderboard")
              : t("kothLeaderboard.leaderboard.dailyHighestStreak")}
          </h2>
          <div className="flex items-center justify-center gap-2">
            <img
              src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
              className="w-3.5"
              alt="Timer"
            />
            <p className="font-extrabold text-white/80 text-base tracking-wider pl-1">
              {(() => {
                const countdown = leaderboardQuery.data?.endTimestamp;
                if (typeof countdown !== "number") {
                  return "0d 0h 0m";
                }
                const secondsRemaining =
                  countdown > 31536000
                    ? Math.max(0, countdown - Math.floor(Date.now() / 1000))
                    : countdown;
                return formatCountdown(secondsRemaining);
              })()}
            </p>
          </div>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          const tabValue = value as "daily" | "highestStreak";
          setActiveTab(tabValue);
          navigate({
            from: Route.fullPath,
            replace: true,
            search: (prev) => ({
              ...prev,
              tab: tabValue,
            }),
          });
        }}
        className="flex-1 min-h-0 flex flex-col"
      >
        <div className="text-center px-2 my-2 absolute left-12 right-0 top-2 z-50">
          <TabsList className="mx-auto w-full h-8.5!">
            <TabsTrigger value="daily" className="uppercase">
              {t("kothLeaderboard.leaderboard.dailyLeaderboard")}
            </TabsTrigger>
            <TabsTrigger value="highestStreak" className="uppercase">
              {t("kothLeaderboard.leaderboard.dailyHighestStreak")}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="daily" className="flex-1 min-h-0 flex flex-col">
          <DailyLeaderboard
            rows={rows}
            isLoading={leaderboardQuery.isLoading}
            error={leaderboardQuery.error as Error | null}
            refetch={leaderboardQuery.refetch}
            stats={stats}
          />
        </TabsContent>

        <TabsContent
          value="highestStreak"
          className="flex-1 min-h-0 flex flex-col"
        >
          <DailyHighestStreak
            rows={rows}
            isLoading={leaderboardQuery.isLoading}
            error={leaderboardQuery.error as Error | null}
            refetch={leaderboardQuery.refetch}
            stats={stats}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
